import { useCallback } from 'react'

import { roleHasPermission, type Permission } from '../permissions/permissions'
import { useAuthStore } from '../store/authStore'

/**
 * Comprobación de permisos del usuario actual.
 *
 * @example
 * const { can, canAny } = usePermissions()
 *
 * if (can(PERMISSIONS.CASH_REGISTER_CLOSE)) { ... }
 *
 * // Para mostrar u ocultar en el JSX, prefiere el componente <Can>:
 * <Can permission={PERMISSIONS.CUSTOMERS_CREATE}>
 *   <Button>Nuevo cliente</Button>
 * </Can>
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user)

  /**
   * `useCallback` mantiene la misma referencia de función entre renders
   * mientras el usuario no cambie. Sin esto, cada render crearía una función
   * nueva y provocaría re-renders innecesarios en los componentes hijos que
   * la reciban por props.
   */
  const can = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false
      return roleHasPermission(user.role, permission)
    },
    [user],
  )

  /** ¿Tiene AL MENOS UNO de estos permisos? Útil para mostrar una sección. */
  const canAny = useCallback(
    (permissions: readonly Permission[]): boolean => permissions.some(can),
    [can],
  )

  /** ¿Tiene TODOS estos permisos? Para acciones que combinan varias cosas. */
  const canAll = useCallback(
    (permissions: readonly Permission[]): boolean => permissions.every(can),
    [can],
  )

  return { can, canAny, canAll, role: user?.role ?? null }
}
